import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  Divider,
  IconButton,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import { useEffect, useState } from "react";
import type { CallContact } from "@shared/types/foyer";
import { foyerApi, type PhotoMailboxInfo } from "../http/foyer";
import { describeError, useToast } from "./toast";
import { Trans, useTranslation } from "react-i18next";
import { PermissionGate } from "../auth/access";
import { useFormat } from "../i18n/format";
import { translateKey } from "../i18n/translate";

const NO_VALUE = "-";

/**
 * A RESIDENT's family-photos mailbox, opened from the Residents roster. Every
 * action here goes to the server straight away.
 *
 * The right to show photos belongs to the resident, not the room (2026-10-19):
 * the address follows the person, the TV shows only the current occupant's
 * photos, and the mailbox closes on discharge. There is no allowlist to edit -
 * the senders are the resident's family contacts with an email address and
 * recorded consent, the same list that governs video calls (calling itself
 * does not need to be enabled).
 *
 * States:
 *   - No mailbox yet  → single "Activate" button that creates one.
 *   - Mailbox exists  → email+copy, stats, photos, who can send, pause and
 *                       moderation toggles, revoke button.
 */
export default function ResidentPhotoMailboxPanel({
  premiseId,
  personRef,
  existing,
  onMailboxChange,
}: {
  premiseId: string;
  personRef: string;
  existing: PhotoMailboxInfo | null;
  /** Called when mailbox is created, updated, or revoked (null = revoked). */
  onMailboxChange: (mb: PhotoMailboxInfo | null) => void;
}) {
  const toast = useToast();
  const { t } = useTranslation(["care", "common"]);
  const fmt = useFormat();
  const [mailbox, setMailbox] = useState<PhotoMailboxInfo | null>(existing);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // All photos sent to this resident (all statuses) - stats + thumbnail grid.
  type RoomPhoto = {
    photoId: string;
    roomId: string;
    receivedAt: string;
    expiresAt: string;
    status: string;
    imageUrl: string;
    fromName: string;
    fromEmail: string;
    subject: string;
    caption: string | null;
  };
  const [roomPhotos, setRoomPhotos] = useState<RoomPhoto[]>([]);
  const [deletingIds, setDeletingIds] = useState<Set<string>>(new Set());
  // Index (into roomPhotos) of the photo opened in the large-format
  // lightbox; null = closed.
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  // Who can send: the resident's family contacts. `null` = not loaded, or the
  // operator may not read the family list (a 403 is an answer, not a fault).
  const [contacts, setContacts] = useState<CallContact[] | null>(null);

  // Revoke confirmation step.
  const [confirmRevoke, setConfirmRevoke] = useState(false);
  const [revoking, setRevoking] = useState(false);

  // When mailbox is known, load the resident's photos and family senders.
  useEffect(() => {
    if (!mailbox) return;
    foyerApi
      .listPremiseCarePhotos(premiseId, { personRef })
      .then((photos) => setRoomPhotos(photos))
      .catch(() => {
        /* non-critical */
      });
    foyerApi
      .listCallContacts(premiseId, { personRef })
      .then((rows) => setContacts(rows))
      .catch(() => setContacts(null));
  }, [mailbox, premiseId, personRef]);

  const familyWithEmail = (contacts ?? []).filter(
    (c) => c.kind === "family" && !c.revokedAt && !!c.endpointHints.email
  );
  const senders = familyWithEmail.filter((c) => !!c.consent);
  const awaitingConsent = familyWithEmail.filter((c) => !c.consent);

  // Stats derived from approved photos only (what's visible on the TV).
  const approvedPhotos = roomPhotos.filter((p) => p.status === "approved");
  const photoCount = approvedPhotos.length;
  const lastReceived =
    approvedPhotos.length > 0
      ? approvedPhotos.reduce((a, b) => (a.receivedAt > b.receivedAt ? a : b))
          .receivedAt
      : null;

  function copyEmail() {
    if (!mailbox?.emailAddress) return;
    void navigator.clipboard.writeText(mailbox.emailAddress).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  async function activate() {
    setLoading(true);
    setError(null);
    try {
      const mb = await foyerApi.activatePhotoMailbox(premiseId, personRef);
      setMailbox(mb);
      onMailboxChange(mb);
      toast.success(
        t("residentPhotoMailboxPanel.created", { email: mb.emailAddress })
      );
    } catch (e) {
      setError(
        e instanceof Error
          ? e.message
          : t("residentPhotoMailboxPanel.createFailed")
      );
      toast.error(describeError(t, "createPhotoMailbox", e));
    } finally {
      setLoading(false);
    }
  }

  async function toggleActive() {
    if (!mailbox) return;
    try {
      const mb = await foyerApi.updatePhotoMailbox(premiseId, personRef, {
        active: !mailbox.active,
      });
      setMailbox(mb);
      onMailboxChange(mb);
      toast.success(
        mb.active
          ? t("residentPhotoMailboxPanel.switchedOn")
          : t("residentPhotoMailboxPanel.switchedOff")
      );
    } catch (e) {
      setError(
        e instanceof Error
          ? e.message
          : t("residentPhotoMailboxPanel.changeFailed")
      );
      toast.error(
        describeError(
          t,
          mailbox.active ? "switchOffPhotoMailbox" : "switchOnPhotoMailbox",
          e
        )
      );
    }
  }

  async function toggleModerated() {
    if (!mailbox) return;
    try {
      const mb = await foyerApi.updatePhotoMailbox(premiseId, personRef, {
        moderated: !mailbox.moderated,
      });
      setMailbox(mb);
      onMailboxChange(mb);
      toast.success(
        mb.moderated
          ? t("residentPhotoMailboxPanel.moderatedOn")
          : t("residentPhotoMailboxPanel.moderatedOff")
      );
    } catch (e) {
      toast.error(describeError(t, "changePhotoApproval", e));
    }
  }

  async function deletePhoto(photoId: string) {
    setDeletingIds((prev) => new Set(prev).add(photoId));
    try {
      await foyerApi.deleteRoomCarePhoto(premiseId, photoId);
      setRoomPhotos((prev) => prev.filter((p) => p.photoId !== photoId));
      toast.success(t("residentPhotoMailboxPanel.deleted"));
    } catch (e) {
      // The thumbnail stays; say why it did.
      toast.error(describeError(t, "deletePhoto", e));
    } finally {
      setDeletingIds((prev) => {
        const next = new Set(prev);
        next.delete(photoId);
        return next;
      });
    }
  }

  // Photo currently shown in the lightbox (derived from index).
  const lightboxPhoto =
    lightboxIndex != null ? (roomPhotos[lightboxIndex] ?? null) : null;

  const showPrevPhoto = () =>
    setLightboxIndex((i) =>
      i == null || roomPhotos.length === 0
        ? i
        : (i - 1 + roomPhotos.length) % roomPhotos.length
    );
  const showNextPhoto = () =>
    setLightboxIndex((i) =>
      i == null || roomPhotos.length === 0 ? i : (i + 1) % roomPhotos.length
    );

  // Delete the photo currently open in the lightbox, then advance to the
  // next one (or close if it was the last).
  async function deleteFromLightbox() {
    if (lightboxIndex == null) return;
    const photo = roomPhotos[lightboxIndex];
    if (!photo) return;
    await deletePhoto(photo.photoId);
    // roomPhotos still holds the pre-delete list inside this closure, so the
    // new length is length - 1; clamp the index into the shortened list.
    const newLen = roomPhotos.length - 1;
    setLightboxIndex(newLen <= 0 ? null : Math.min(lightboxIndex, newLen - 1));
  }

  // Arrow-key navigation while the lightbox is open.
  useEffect(() => {
    if (lightboxIndex == null) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "ArrowLeft") showPrevPhoto();
      else if (e.key === "ArrowRight") showNextPhoto();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lightboxIndex, roomPhotos.length]);

  async function revoke() {
    setRevoking(true);
    try {
      await foyerApi.revokePhotoMailbox(premiseId, personRef);
      onMailboxChange(null);
      // Back to the "Activate" state; the drawer stays open.
      setMailbox(null);
      setRoomPhotos([]);
      setRevoking(false);
      setConfirmRevoke(false);
      toast.success(t("residentPhotoMailboxPanel.revoked"));
    } catch (e) {
      setError(
        e instanceof Error
          ? e.message
          : t("residentPhotoMailboxPanel.revokeFailed")
      );
      toast.error(describeError(t, "revokePhotoMailbox", e));
      setRevoking(false);
      setConfirmRevoke(false);
    }
  }

  const formatDate = (iso: string) =>
    fmt.date(iso, { day: "numeric", month: "short", year: "numeric" });

  const formatDateTime = (iso: string) =>
    fmt.dateTime(iso, {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

  return (
    <Box data-testid={`resident-photo-mailbox-${personRef}`}>
      <Stack spacing={2}>
        {/* ── No mailbox yet ─────────────────────────────────────── */}
        {!mailbox && (
          <>
            <Typography variant="body2" color="text.secondary">
              {t("residentPhotoMailboxPanel.intro")}
            </Typography>
            {error && (
              <Alert severity="error" sx={{ py: 0 }}>
                {error}
              </Alert>
            )}
            <Stack direction="row" justifyContent="flex-end" spacing={1}>
              <PermissionGate
                resource="residents/photos"
                level="edit"
                place={{ premiseId }}
              >
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => void activate()}
                  disabled={loading}
                  startIcon={
                    loading ? <CircularProgress size={14} /> : undefined
                  }
                >
                  {t("residentPhotoMailboxPanel.activate")}
                </Button>
              </PermissionGate>
            </Stack>
          </>
        )}

        {/* ── Mailbox exists ─────────────────────────────────────── */}
        {mailbox && (
          <>
            {/* Email address */}
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{
                px: 1.5,
                py: 1,
                bgcolor: "action.hover",
                borderRadius: 1,
                border: 1,
                borderColor: "divider",
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  flex: 1,
                  fontFamily: "fontFamilyMono",
                  typography: "meta",
                  wordBreak: "break-all",
                  color: mailbox.active ? "text.primary" : "text.disabled",
                }}
              >
                {mailbox.emailAddress}
              </Typography>
              <Tooltip
                title={
                  copied
                    ? t("residentPhotoMailboxPanel.copied")
                    : t("common:buttons.copy")
                }
              >
                <IconButton size="small" onClick={copyEmail}>
                  {copied ? (
                    <CheckIcon fontSize="small" color="success" />
                  ) : (
                    <ContentCopyIcon fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
            </Stack>

            {/* Stats */}
            <Stack direction="row" spacing={3}>
              <Stack>
                <Typography variant="caption" color="text.secondary">
                  {t("residentPhotoMailboxPanel.photosInSlideshow")}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: "fontWeightSemibold" }}
                >
                  {photoCount}
                </Typography>
              </Stack>
              <Stack>
                <Typography variant="caption" color="text.secondary">
                  {t("residentPhotoMailboxPanel.lastReceived")}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: "fontWeightSemibold" }}
                >
                  {lastReceived ? formatDate(lastReceived) : NO_VALUE}
                </Typography>
              </Stack>
            </Stack>

            {/* Photo thumbnails */}
            {roomPhotos.length > 0 && (
              <Stack spacing={0.5}>
                <Typography variant="caption" color="text.secondary">
                  {t("residentPhotoMailboxPanel.photosCount", {
                    count: roomPhotos.length,
                  })}
                </Typography>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(4, 1fr)",
                    gridAutoRows: "max-content",
                    gap: 0.75,
                  }}
                >
                  {roomPhotos.map((photo, photoIdx) => (
                    <Box
                      key={photo.photoId}
                      sx={{
                        position: "relative",
                        aspectRatio: "1",
                        borderRadius: 1,
                        overflow: "hidden",
                        bgcolor: "action.hover",
                        border: 1,
                        borderColor:
                          photo.status === "approved"
                            ? "divider"
                            : photo.status === "pending"
                              ? "warning.light"
                              : "error.light",
                      }}
                    >
                      <Box
                        component="img"
                        src={photo.imageUrl}
                        alt={photo.caption ?? photo.fromName}
                        onClick={() => setLightboxIndex(photoIdx)}
                        sx={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          display: "block",
                          cursor: "pointer",
                        }}
                      />
                      <PermissionGate
                        resource="residents/photos"
                        level="edit"
                        place={{ premiseId }}
                      >
                        <Tooltip
                          title={
                            photo.status === "pending"
                              ? t("residentPhotoMailboxPanel.deletePending")
                              : photo.status === "rejected"
                                ? t("residentPhotoMailboxPanel.deleteRejected")
                                : t("common:buttons.delete")
                          }
                        >
                          <IconButton
                            size="small"
                            onClick={() => void deletePhoto(photo.photoId)}
                            disabled={deletingIds.has(photo.photoId)}
                            sx={{
                              position: "absolute",
                              top: 2,
                              right: 2,
                              width: 22,
                              height: 22,
                              bgcolor: "rgba(0,0,0,0.55)",
                              color: "#fff",
                              "&:hover": { bgcolor: "rgba(200,0,0,0.8)" },
                              p: 0,
                            }}
                          >
                            {deletingIds.has(photo.photoId) ? (
                              <CircularProgress
                                size={12}
                                sx={{ color: "#fff" }}
                              />
                            ) : (
                              <DeleteIcon sx={{ fontSize: 14 }} />
                            )}
                          </IconButton>
                        </Tooltip>
                      </PermissionGate>
                    </Box>
                  ))}
                </Box>
              </Stack>
            )}

            {/* Who can send */}
            <Stack spacing={0.5} data-testid="photo-senders">
              <Typography variant="caption" color="text.secondary">
                {t("residentPhotoMailboxPanel.whoCanSend")}
              </Typography>
              {contacts === null ? (
                <Typography variant="body2" color="text.secondary">
                  {t("residentPhotoMailboxPanel.sendersUnknown")}
                </Typography>
              ) : senders.length === 0 ? (
                <Alert severity="warning" sx={{ py: 0 }}>
                  {t("residentPhotoMailboxPanel.noSenders")}
                </Alert>
              ) : (
                <Stack spacing={0.25}>
                  {senders.map((c) => (
                    <Typography key={c.id} variant="body2">
                      <Trans
                        t={t}
                        i18nKey="residentPhotoMailboxPanel.senderLine"
                        values={{
                          name: c.relation
                            ? t("callLogViewer.contactWithRelation", {
                                name: c.displayName,
                                relation: c.relation,
                              })
                            : c.displayName,
                          email: c.endpointHints.email,
                        }}
                        components={{
                          muted: (
                            <Box
                              component="span"
                              sx={{ color: "text.secondary" }}
                            />
                          ),
                        }}
                      />
                    </Typography>
                  ))}
                </Stack>
              )}
              {awaitingConsent.length > 0 && (
                <Typography variant="caption" color="warning.main">
                  {t("residentPhotoMailboxPanel.awaitingConsent", {
                    count: awaitingConsent.length,
                  })}
                </Typography>
              )}
            </Stack>

            {/* Pause / Resume toggle */}
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Stack>
                <Typography variant="body2">
                  {mailbox.active
                    ? t("residentPhotoMailboxPanel.accepting")
                    : t("residentPhotoMailboxPanel.paused")}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {mailbox.active
                    ? t("residentPhotoMailboxPanel.acceptingHelp")
                    : t("residentPhotoMailboxPanel.pausedHelp")}
                </Typography>
              </Stack>
              <PermissionGate
                resource="residents/photos"
                level="edit"
                place={{ premiseId }}
                deniedProps={{ disabled: true }}
              >
                <Switch
                  checked={mailbox.active}
                  onChange={() => void toggleActive()}
                  size="small"
                  inputProps={
                    {
                      "aria-label": t(
                        "residentPhotoMailboxPanel.togglePhotoSharing"
                      ),
                      "data-testid": `mailbox-active-${personRef}`,
                    } as React.InputHTMLAttributes<HTMLInputElement>
                  }
                />
              </PermissionGate>
            </Stack>

            {/* Moderation toggle */}
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Stack>
                <Typography variant="body2">
                  {mailbox.moderated
                    ? t("residentPhotoMailboxPanel.reviewBefore")
                    : t("residentPhotoMailboxPanel.showAuto")}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {mailbox.moderated
                    ? t("residentPhotoMailboxPanel.reviewHelp")
                    : t("residentPhotoMailboxPanel.showAutoHelp")}
                </Typography>
              </Stack>
              <PermissionGate
                resource="residents/photos"
                level="edit"
                place={{ premiseId }}
                deniedProps={{ disabled: true }}
              >
                <Switch
                  checked={mailbox.moderated}
                  onChange={() => void toggleModerated()}
                  size="small"
                  inputProps={
                    {
                      "aria-label": t(
                        "residentPhotoMailboxPanel.toggleModeration"
                      ),
                      "data-testid": `mailbox-moderated-${personRef}`,
                    } as React.InputHTMLAttributes<HTMLInputElement>
                  }
                />
              </PermissionGate>
            </Stack>

            {error && (
              <Alert severity="error" sx={{ py: 0 }}>
                {error}
              </Alert>
            )}

            {/* Revoke */}
            {!confirmRevoke ? (
              <Stack direction="row" justifyContent="space-between" pt={0.5}>
                <PermissionGate
                  resource="residents/photos"
                  level="edit"
                  place={{ premiseId }}
                >
                  <Button
                    size="small"
                    color="error"
                    onClick={() => setConfirmRevoke(true)}
                    data-testid={`mailbox-revoke-${personRef}`}
                  >
                    {t("residentPhotoMailboxPanel.revokeSharing")}
                  </Button>
                </PermissionGate>
              </Stack>
            ) : (
              <Stack spacing={1}>
                <Alert severity="warning" sx={{ py: 0.5 }}>
                  {t("residentPhotoMailboxPanel.revokeWarning")}
                </Alert>
                <Stack direction="row" justifyContent="flex-end" spacing={1}>
                  <Button
                    size="small"
                    onClick={() => setConfirmRevoke(false)}
                    disabled={revoking}
                  >
                    {t("common:buttons.cancel")}
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    color="error"
                    onClick={() => void revoke()}
                    disabled={revoking}
                    startIcon={
                      revoking ? <CircularProgress size={14} /> : undefined
                    }
                  >
                    {revoking
                      ? t("residentPhotoMailboxPanel.revoking")
                      : t("residentPhotoMailboxPanel.yesRevoke")}
                  </Button>
                </Stack>
              </Stack>
            )}
          </>
        )}
      </Stack>

      {/* ── Large-format photo viewer ──────────────────────────────── */}
      <Dialog
        open={!!lightboxPhoto}
        onClose={() => setLightboxIndex(null)}
        maxWidth="md"
        fullWidth
        data-testid="care-photo-lightbox"
      >
        {lightboxPhoto && (
          <DialogContent sx={{ p: 0 }}>
            <Box sx={{ position: "relative", bgcolor: "#000" }}>
              <Box
                component="img"
                src={lightboxPhoto.imageUrl}
                alt={lightboxPhoto.caption ?? lightboxPhoto.fromName}
                sx={{
                  display: "block",
                  width: "100%",
                  maxHeight: "70vh",
                  objectFit: "contain",
                }}
              />
              <IconButton
                onClick={() => setLightboxIndex(null)}
                aria-label={t("common:buttons.close")}
                sx={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                  bgcolor: "rgba(0,0,0,0.55)",
                  color: "#fff",
                  "&:hover": { bgcolor: "rgba(0,0,0,0.75)" },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>

              {/* Prev / Next navigation (only when >1 photo) */}
              {roomPhotos.length > 1 && (
                <>
                  <IconButton
                    onClick={showPrevPhoto}
                    aria-label={t("residentPhotoMailboxPanel.previous")}
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: 8,
                      transform: "translateY(-50%)",
                      bgcolor: "rgba(0,0,0,0.55)",
                      color: "#fff",
                      "&:hover": { bgcolor: "rgba(0,0,0,0.75)" },
                    }}
                  >
                    <ChevronLeftIcon />
                  </IconButton>
                  <IconButton
                    onClick={showNextPhoto}
                    aria-label={t("residentPhotoMailboxPanel.next")}
                    sx={{
                      position: "absolute",
                      top: "50%",
                      right: 8,
                      transform: "translateY(-50%)",
                      bgcolor: "rgba(0,0,0,0.55)",
                      color: "#fff",
                      "&:hover": { bgcolor: "rgba(0,0,0,0.75)" },
                    }}
                  >
                    <ChevronRightIcon />
                  </IconButton>
                  <Typography
                    variant="caption"
                    sx={{
                      position: "absolute",
                      bottom: 8,
                      left: "50%",
                      transform: "translateX(-50%)",
                      px: 1,
                      py: 0.25,
                      borderRadius: 1,
                      bgcolor: "rgba(0,0,0,0.55)",
                      color: "#fff",
                    }}
                  >
                    {t("residentPhotoMailboxPanel.counter", {
                      index: (lightboxIndex ?? 0) + 1,
                      total: roomPhotos.length,
                    })}
                  </Typography>
                </>
              )}
            </Box>
            <Stack spacing={1.5} sx={{ p: 2.5 }}>
              <Stack
                direction="row"
                alignItems="flex-start"
                justifyContent="space-between"
                spacing={1}
              >
                <Stack spacing={1}>
                  {lightboxPhoto.caption && (
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: "fontWeightSemibold" }}
                    >
                      {lightboxPhoto.caption}
                    </Typography>
                  )}
                  <Chip
                    size="small"
                    label={translateKey(
                      t,
                      `care:residentPhotoMailboxPanel.status.${lightboxPhoto.status}`,
                      { defaultValue: lightboxPhoto.status }
                    )}
                    color={
                      lightboxPhoto.status === "approved"
                        ? "success"
                        : lightboxPhoto.status === "pending"
                          ? "warning"
                          : "error"
                    }
                    variant="outlined"
                    sx={{ alignSelf: "flex-start" }}
                  />
                </Stack>
                <PermissionGate
                  resource="residents/photos"
                  level="edit"
                  place={{ premiseId }}
                >
                  <Button
                    size="small"
                    color="error"
                    variant="outlined"
                    startIcon={
                      lightboxPhoto &&
                      deletingIds.has(lightboxPhoto.photoId) ? (
                        <CircularProgress size={14} />
                      ) : (
                        <DeleteIcon />
                      )
                    }
                    onClick={() => void deleteFromLightbox()}
                    disabled={
                      !!lightboxPhoto && deletingIds.has(lightboxPhoto.photoId)
                    }
                    sx={{ flexShrink: 0 }}
                  >
                    {t("common:buttons.delete")}
                  </Button>
                </PermissionGate>
              </Stack>
              <Divider />
              <Stack spacing={1.25}>
                <PhotoMetaRow
                  label={t("residentPhotoMailboxPanel.meta.from")}
                  value={
                    lightboxPhoto.fromName
                      ? t("residentPhotoMailboxPanel.meta.fromLine", {
                          name: lightboxPhoto.fromName,
                          email: lightboxPhoto.fromEmail,
                        })
                      : lightboxPhoto.fromEmail
                  }
                />
                {lightboxPhoto.subject && (
                  <PhotoMetaRow
                    label={t("residentPhotoMailboxPanel.meta.subject")}
                    value={lightboxPhoto.subject}
                  />
                )}
                <PhotoMetaRow
                  label={t("residentPhotoMailboxPanel.meta.received")}
                  value={formatDateTime(lightboxPhoto.receivedAt)}
                />
                {lightboxPhoto.expiresAt && (
                  <PhotoMetaRow
                    label={t("residentPhotoMailboxPanel.meta.expires")}
                    value={formatDateTime(lightboxPhoto.expiresAt)}
                  />
                )}
              </Stack>
            </Stack>
          </DialogContent>
        )}
      </Dialog>
    </Box>
  );
}

/** One label/value line in the photo lightbox metadata panel. */
function PhotoMetaRow({ label, value }: { label: string; value: string }) {
  return (
    <Stack direction="row" spacing={2}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ minWidth: 88, flexShrink: 0, pt: 0.25 }}
      >
        {label}
      </Typography>
      <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
        {value}
      </Typography>
    </Stack>
  );
}
